import { type TeamMember } from "@/types";
import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Check, X, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TeamMemberListProps {
  members: TeamMember[];
  onMemberRemoved: () => void;
}

export const TeamMemberList = ({
  members,
  onMemberRemoved,
}: TeamMemberListProps) => {
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    fetchCurrentUser();
  }, []);
  const startEditing = (member: TeamMember) => {
    setEditingMemberId(member.id);
    setEditingName(member.name || member.email);
  };

  const cancelEditing = () => {
    setEditingMemberId(null);
    setEditingName("");
  };

  const saveNameEdit = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from("team_members")
        .update({ name: editingName.trim() })
        .eq("id", memberId);

      if (error) {
        console.error("Error updating name:", error);
        toast.error("Failed to update name");
        return;
      }

      toast.success("Name updated successfully");
      setEditingMemberId(null);
      setEditingName("");
      onMemberRemoved(); // Refresh the list
    } catch (error) {
      console.error("Error in saveNameEdit:", error);
      toast.error("Something went wrong");
    }
  };

  const handleRemoveMember = async (memberId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from("team_members")
        .delete()
        .eq("id", memberId);

      // Remove company id from user profile
      const { error: removeProfileError } = await supabase
        .from("profiles")
        .update({ company_id: null })
        .eq("id", userId);

      if (removeProfileError) {
        console.error(
          "Error removing company id from profile:",
          removeProfileError
        );
      }

      if (error) {
        console.error("Error removing team member:", error);
        toast.error("Failed to remove team member");
        return;
      }

      toast.success("Team member removed successfully");
      onMemberRemoved();
    } catch (error) {
      console.error("Error in handleRemoveMember:", error);
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="w-[150px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => {
            const isCurrentUser = member.user_id === currentUserId;
            const isEditing = editingMemberId === member.id;

            return (
              <TableRow key={member.id}>
                <TableCell className="flex items-center gap-4">
                  <Avatar>
                    <AvatarImage src={member.profile?.avatar_url || undefined} />
                    <AvatarFallback>
                      {(member.name || member.email)?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="h-8"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              saveNameEdit(member.id);
                            } else if (e.key === 'Escape') {
                              cancelEditing();
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="font-medium flex items-center gap-2">
                          {member.name || member.email}
                          {isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => startEditing(member)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {member.email}
                        </div>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={member.status === "active" ? "default" : "secondary"}
                  >
                    {member.status === "active" ? "Active" : "Pending"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(member.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => saveNameEdit(member.id)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={cancelEditing}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveMember(member.id, member.user_id)}
                        className="text-destructive hover:text-destructive/90"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
